import { ApolloClient, NormalizedCacheObject } from "@apollo/client";
import { AgGrid } from "@jsq/ds/core";
import { client } from "~/apollo/client";
import { TransactionLookupDocument } from "~/graphql/supergraphHooks";
import { toFederationGlobalId } from "~/utils/graphql-helpers";
import { TransactionLookupFilters } from "./types";
import type {
  TransactionLookupQuery,
  TransactionLookupQueryVariables,
} from "~/graphql/supergraphTypes";

interface DataSourceCallbacks {
  onError?: (message: string) => void;
  onSuccess?: () => void;
  /** True when a newer filter generation replaced this datasource. */
  isStale?: () => boolean;
}

export class TransactionLookupDataSource implements AgGrid.IServerSideDatasource {
  private client: ApolloClient<NormalizedCacheObject>;
  private filters: TransactionLookupFilters;
  private callbacks: DataSourceCallbacks;
  private inflightId = 0;

  constructor(filters: TransactionLookupFilters, callbacks: DataSourceCallbacks = {}) {
    this.client = client;
    this.filters = filters;
    this.callbacks = callbacks;
  }

  private buildVariables(
    offset: number,
    limit: number,
    sortBy: string | null = null,
    sortDirection: string = "desc",
  ): TransactionLookupQueryVariables {
    const globalArenaId = toFederationGlobalId(
      "ArenaObjectType",
      parseInt(this.filters.arenaId, 10),
    );

    return {
      arenaId: globalArenaId,
      entityId: this.filters.entityId,
      includeDeleted: this.filters.includeDeleted,
      offset,
      limit,
      glDateFrom: this.filters.glDateFrom ?? null,
      glDateTo: this.filters.glDateTo ?? null,
      effectiveDateFrom: this.filters.effectiveDateFrom ?? null,
      effectiveDateTo: this.filters.effectiveDateTo ?? null,
      status: this.filters.status ?? null,
      batchSourceObjectId: this.filters.batchSourceObjectId ?? null,
      glTransactionType: this.filters.glTransactionType ?? null,
      hasMapping: this.filters.hasMapping ?? null,
      quickSearch: this.filters.quickSearch ?? null,
      sortBy,
      sortDirection,
    };
  }

  public getRows(params: AgGrid.IServerSideGetRowsParams): void {
    const offset = params.request.startRow ?? 0;
    const limit = (params.request.endRow ?? 50) - offset;
    const requestId = ++this.inflightId;

    // Column filters are disabled in columnDefs — FilterPanel owns filtering.
    // Only sortModel is read from the AG Grid request.
    const sortModel = params.request.sortModel;
    const sortBy = sortModel && sortModel.length > 0 ? sortModel[0].colId : null;
    const sortDirection =
      sortModel && sortModel.length > 0 ? (sortModel[0].sort ?? "desc") : "desc";

    this.client
      .query<TransactionLookupQuery>({
        query: TransactionLookupDocument,
        variables: this.buildVariables(offset, limit, sortBy, sortDirection),
        fetchPolicy: "no-cache",
      })
      .then((response) => {
        if (this.callbacks.isStale?.() || requestId !== this.inflightId) return;

        const { transactions, totalCount } = response.data.transactionLookup;
        this.callbacks.onSuccess?.();

        params.success({
          rowData: transactions || [],
          rowCount: totalCount,
        });
      })
      .catch((err) => {
        if (this.callbacks.isStale?.() || requestId !== this.inflightId) return;

        const message =
          err?.graphQLErrors?.[0]?.message ??
          err?.message ??
          "Failed to load transactions. Please try again.";
        this.callbacks.onError?.(message);
        params.fail();
      });
  }
}
